import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import config from '../config.js';
import { apiErrorMessage, detailToString } from '../utils/apiError';
// The list query, reads, Pre-ship, file upload, field edits and task video are shared with the
// phone Jobs screens (features/jobs/, docs/mobile-pwa-plan.md "One copy of every action").
import { jobListParams, loadJobList, sortJobs, loadCustomersByName, loadJobDocuments, loadShipParts,
  canEditDueDate, canPreShip, defaultListStartDate, JOB_LIST_STATIONS } from '../features/jobs/jobsData';
import { patchJob, remainingToShip, preShipItems, preShip, uploadJobDocument } from '../features/jobs/jobActions';
import { loadCameraConfig, canShowTaskVideo, taskCameraCount, listTaskClips, clipCount, loadClipUrls, clipTimeCT } from '../features/jobs/taskVideo';
import { desktopUi } from '../features/desktopUi';
import { canOfferCutSheetSwitch } from '../features/jobs/cutSheetSwitch';
import CutSheetSwitchModal from '../components/CutSheetSwitchModal.jsx';

// --- NEW: Debounce Hook ---
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/*─────────────────────────── DATE HELPERS ───────────────────────────*/

function parseLocalDateOnly(str) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) return null;
  const [, y, mo, d] = m.map(Number);
  return new Date(y, mo - 1, d, 0, 0, 0, 0);
}

function formatTimestamp(val) {
  if (!val) return "--";
  const dt = parseLocalDateOnly(val) || new Date(val);
  if (isNaN(dt.getTime())) return "--";
  return dt.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateOnly(val) {
  if (!val) return "--";
  const dt = parseLocalDateOnly(val) || new Date(val);
  if (isNaN(dt.getTime())) return "--";
  return dt.toLocaleDateString("en-US");
}

/*─────────────────────────── TIME HELPERS ───────────────────────────*/

function formatSecondsToHM(totalSeconds) {
  if (totalSeconds == null) return "--";
  if (totalSeconds < 1) return "0H 0M";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${h}H ${m}M`;
}

function intervalToSeconds(intervalStr) {
    if (!intervalStr) return 0;
    if (typeof intervalStr === 'number') return intervalStr;
    const parts = String(intervalStr).split(":");
    if (parts.length === 3) {
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      const s = parseInt(parts[2], 10) || 0;
      return h * 3600 + m * 60 + s;
    }
    return 0;
}

/*─────────────────────────── DUE DATE DISPLAY ───────────────────────────*/

function DueDateDisplay({ dueDate, status }) {
  const formatted = dueDate ? formatDateOnly(dueDate) : "N/A";
  if (!dueDate || ["Completed", "Shipped", "Cancelled"].includes(status)) {
    return <span>{formatted}</span>;
  }
  const due = parseLocalDateOnly(dueDate) || new Date(dueDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  let color = "inherit";
  let fontWeight = "normal";
  if (diffDays < 0) { color = "#dc3545"; fontWeight = "bold"; }
  else if (diffDays === 0) { color = "#dc3545"; fontWeight = "bold"; }
  else if (diffDays <= 2) { color = "#fd7e14"; fontWeight = "600"; }
  return <span style={{ color, fontWeight }}>{formatted}</span>;
}

/* Camera relay: features/jobs/taskVideo.js (clip times: clipTimeCT, UTC-correct). */

function formatTimeCT(isoStr) {
  if (!isoStr) return "";
  const dt = new Date(isoStr);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  }).format(dt);
}

/*─────────────────────────── HELPER: FILE BADGES ───────────────────────────*/
const getFileTypeBadge = (filename) => {
  if (!filename) return { label: '???', color: 'bg-secondary' };
  const ext = filename.split('.').pop().toLowerCase();
  switch (ext) {
    case 'pdf': return { label: 'PDF', color: 'bg-danger' };
    case 'png':
    case 'jpg':
    case 'jpeg': return { label: 'IMG', color: 'bg-success' };
    case 'xls':
    case 'xlsx':
    case 'csv': return { label: 'EXCEL', color: 'bg-success' };
    case 'doc':
    case 'docx': return { label: 'DOC', color: 'bg-primary' };
    case 'dxf':
    case 'dwg': return { label: 'CAD', color: 'bg-dark' };
    default: return { label: ext.toUpperCase().substring(0, 4), color: 'bg-secondary' };
  }
};

/*─────────────────────────── STATUS BADGE ───────────────────────────*/

function StatusBadge({ status }) {
  const colorMap = {
    Completed: { bg: "#198754", color: "#fff" },
    Shipped: { bg: "#198754", color: "#fff" },
    "In Progress": { bg: "#fd7e14", color: "#fff" },
    Paused: { bg: "#6c757d", color: "#fff" },
    Pending: { bg: "#495057", color: "#e9ecef" },
    Cancelled: { bg: "#dc3545", color: "#fff" },
  };
  const s = colorMap[status] || { bg: "#6c757d", color: "#fff" };
  return <span className="badge" style={{ backgroundColor: s.bg, color: s.color, fontSize: "0.75em" }}>{status}</span>;
}

/*─────────────────────────── SEARCH HIGHLIGHT ───────────────────────*/

function highlightSearch(term, rawText) {
  if (!term) return rawText;
  const text = rawText != null ? String(rawText) : "";
  const parts = text.split(new RegExp(`(${term})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <mark key={i}>{part}</mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

/*─────────────────────────── PROGRESS BAR ───────────────────────────*/

function JobProgress({ tasks }) {
  if (!tasks || tasks.length === 0) return null;
  const stationOrder = ["Laser", "Tap", "Brake", "Fabrication", "Blast", "Finish", "Shipping"];

  const stationsPresent = stationOrder.filter((st) =>
    tasks.some((t) => t.workstation === st)
  );

  if (stationsPresent.length === 0) return null;
  
  const statuses = tasks.reduce((acc, t) => {
    acc[t.workstation] = t.status;
    return acc;
  }, {});

  let currentIndex = stationsPresent.findIndex((st) => {
    const stStatus = statuses[st];
    return stStatus && !/completed|shipped|done|cancelled/i.test(stStatus.toLowerCase());
  });

  if (currentIndex === -1) {
      currentIndex = stationsPresent.length;
  }

  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between mb-1">
        {stationsPresent.map((station, idx) => {
          let circleClass = "bg-light text-dark";
          if (idx < currentIndex) {
            circleClass = "bg-success";
          } else if (idx === currentIndex) {
            circleClass = "bg-info";
          }
          return (
            <div key={station} className="text-center" style={{ flex: 1 }}>
              <div
                className={`rounded-circle mx-auto mb-1 ${circleClass}`}
                style={{ width: "16px", height: "16px" }}
              />
              <small>{station}</small>
            </div>
          );
        })}
      </div>
      <div className="progress" style={{ height: "4px" }}>
        <div
          className="progress-bar bg-success"
          role="progressbar"
          style={{
            width: `${(currentIndex / stationsPresent.length) * 100}%`,
          }}
        />
      </div>
    </div>
  );
}

/*─────────────────────────── EXPANDED DETAIL ────────────────────────*/

function ExpandedJobDetail({ job, apiBaseUrl, onJobChanged }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [switchTaskId, setSwitchTaskId] = useState(null);
  const [cutSheetData, setCutSheetData] = useState(null);
  const [users, setUsers] = useState([]);

  // --- Editing States ---
  const [editMode, setEditMode] = useState({
    shipping_method: false,
    customer_po: false,
    customer_so: false,
    notes: false,
    priority_level: false
  });

  const [editValues, setEditValues] = useState({
    shipping_method: job.shipping_method || "Customer Pickup",
    customer_po: job.customer_po || "",
    customer_so: job.customer_so || "",
    notes: job.notes || "",
    priority_level: job.priority_level || 4
  });

  const [isSaving, setIsSaving] = useState(false);

  // --- File States ---
  const [jobDocuments, setJobDocuments] = useState([]);
  const [docUploadFile, setDocUploadFile] = useState(null);
  const [docDisplayName, setDocDisplayName] = useState("");
  const [docDescription, setDocDescription] = useState("");
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);

  // --- Video Playback States ---
  const [cameraConfig, setCameraConfig] = useState(null);
  const [videoTask, setVideoTask] = useState(null);
  const [clipsByCamera, setClipsByCamera] = useState({});
  const [currentClipIdx, setCurrentClipIdx] = useState(0);
  const [videoUrls, setVideoUrls] = useState({});
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState("");
  const videoRefs = useRef({});
  const blobUrlsRef = useRef({});

  const tasks = job.tasks || [];
  const parts = job.parts || [];
  const laserTask = tasks.find((t) => t.workstation === "Laser");
  const otherUnlinkedTasks = tasks.filter((t) => !t.part_id && t.workstation !== "Laser");

  // --- Load Initial Data ---
  useEffect(() => {
    // 1. Load Users
    fetch(`${apiBaseUrl}/users`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setUsers(data.filter(u => u.is_active)))
      .catch(err => console.error("Error loading users", err));

    // 2. Load Cut Sheet Image
    // Fix: Check if laserTask and cut_sheet_id exist before fetching
    if (laserTask && laserTask.cut_sheet_id) {
      console.log(`Fetching image for Cut Sheet ID: ${laserTask.cut_sheet_id}`);
      fetch(`${apiBaseUrl}/cut_sheets/${laserTask.cut_sheet_id}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
            if(data) {
                setCutSheetData(data);
            } else {
                console.log("Cut sheet found, but no data returned from API.");
                setCutSheetData(null);
            }
        })
        .catch(err => console.error("Error fetching cut sheet:", err));
    } else {
        setCutSheetData(null);
    }

    // 3. Load Job Documents
    refreshJobDocuments();

    // 4. Load Camera Config
    loadCameraConfig().then(data => { if (data) setCameraConfig(data); });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laserTask?.task_id, laserTask?.cut_sheet_id, apiBaseUrl, job.job_id]);
  // ^ FIX: Dependent on IDs (primitives), not the laserTask object reference

  const refreshJobDocuments = async () => {
    try {
      const docs = await loadJobDocuments(job.job_id);
      if (docs) setJobDocuments(docs);
    } catch (err) {
      console.error("Failed to load documents", err);
    }
  };

  // --- Handle Auth User Change ---
  const handleAuthUserChange = async (taskId, userIdStr) => {
    const userId = userIdStr === "" ? null : parseInt(userIdStr);
    try {
      const res = await fetch(`${apiBaseUrl}/tasks/${taskId}/authorized_user`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authorized_user_id: userId })
      });
      if (!res.ok) throw new Error("Failed to update authorized user");
      
      const task = tasks.find(t => t.task_id === taskId);
      if (task) task.authorized_user_id = userId;
      setUsers([...users]); 
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleSaveField = async (field) => {
    setIsSaving(true);
    try {
      const payload = { [field]: editValues[field] };
      await patchJob(job.job_id, payload); // a refusal now says the API's reason
      job[field] = editValues[field];
      setEditMode(prev => ({ ...prev, [field]: false })); 
      if(field === 'priority_level') alert("Priority updated.");
    } catch (err) {
      alert("Error updating: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleEdit = (field) => {
    setEditMode(prev => ({ ...prev, [field]: !prev[field] }));
    if (!editMode[field]) {
      setEditValues(prev => ({ ...prev, [field]: job[field] || "" }));
    }
  };

  async function handleUploadDocument() {
    const result = await uploadJobDocument({
      jobId: job.job_id, file: docUploadFile, displayName: docDisplayName, description: docDescription,
      ui: desktopUi, setBusy: setIsUploadingDoc,
    });
    if (result.reload) {
      setDocUploadFile(null); setDocDisplayName(""); setDocDescription("");
      refreshJobDocuments();
    }
  }

  async function handleDeleteDocument(docId) {
    if (!window.confirm("Delete this file?")) return;
    try {
      const res = await fetch(`${apiBaseUrl}/jobs/${job.job_id}/documents/${docId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      refreshJobDocuments();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  }

  // --- Video Playback Helpers ---
  const revokeAllBlobs = useCallback(() => {
    Object.values(blobUrlsRef.current).forEach(url => {
      try { URL.revokeObjectURL(url); } catch (e) { /* noop */ }
    });
    blobUrlsRef.current = {};
    setVideoUrls({});
  }, []);

  const canShowVideo = (task) => canShowTaskVideo(task, cameraConfig);

  const loadClipBlobs = async (clips, clipIdx, station) => {
    revokeAllBlobs();
    setVideoLoading(true);
    setVideoError("");
    const stationName = station || videoTask?.workstation || "";
    try {
      const newUrls = await loadClipUrls(clips, clipIdx, stationName);
      const newBlobRefs = { ...newUrls };
      if (Object.keys(newUrls).length === 0) setVideoError("Failed to load video clips.");
      blobUrlsRef.current = newBlobRefs;
      setVideoUrls(newUrls);
    } catch { setVideoError("Failed to load video clips."); }
    finally { setVideoLoading(false); }
  };

  const openVideoModal = async (task) => {
    if (!cameraConfig) return;
    const camCount = taskCameraCount(task, cameraConfig);
    if (camCount === 0) return;
    setVideoTask(task);
    setVideoError("");
    setVideoLoading(true);
    setCurrentClipIdx(0);
    revokeAllBlobs();
    try {
      const allClips = await listTaskClips(task, camCount);
      setClipsByCamera(allClips);
      const totalClips = clipCount(allClips);
      if (totalClips === 0) { setVideoError("No recordings found for this task's time range."); setVideoLoading(false); return; }
      await loadClipBlobs(allClips, 0, task.workstation);
    } catch { setVideoError("Failed to fetch recordings."); }
    finally { setVideoLoading(false); }
  };

  const maxClipCount = clipCount(clipsByCamera);

  const goToClip = async (newIdx) => {
    if (newIdx < 0 || newIdx >= maxClipCount) return;
    setCurrentClipIdx(newIdx);
    await loadClipBlobs(clipsByCamera, newIdx);
  };

  const handleVideoEnded = () => {
    if (currentClipIdx < maxClipCount - 1) goToClip(currentClipIdx + 1);
  };

  const closeVideoModal = () => {
    setVideoTask(null);
    setClipsByCamera({});
    setCurrentClipIdx(0);
    setVideoError("");
    setVideoLoading(false);
    revokeAllBlobs();
    videoRefs.current = {};
  };

  const videoCamCount = videoTask ? (cameraConfig?.[videoTask.workstation] || 0) : 0;

  const renderAuthUserSelect = (task) => (
    <select 
      className="form-select form-select-sm" 
      style={{maxWidth: '120px', fontSize: '0.8rem', padding: '2px 5px', height:'24px'}}
      value={task.authorized_user_id || ""}
      onChange={(e) => handleAuthUserChange(task.task_id, e.target.value)}
    >
      <option value="">All Users</option>
      {users.map(u => (
        <option key={u.user_id} value={u.user_id}>{u.name}</option>
      ))}
    </select>
  );

  return (
    <div>
      {/* --- Header Fields (Shipping, PO, Priority, Notes) --- */}
      <div className="row mb-4">
        {/* Shipping Method */}
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title text-muted small">Shipping Method</h6>
              {editMode.shipping_method ? (
                <div className="input-group input-group-sm">
                  <select className="form-select" value={editValues.shipping_method} onChange={(e) => setEditValues({...editValues, shipping_method: e.target.value})}>
                    <option value="Customer Pickup">Customer Pickup</option>
                    <option value="Delivery">Delivery</option>
                    <option value="Freight">Freight</option>
                    <option value="Ground Shipping">Ground Shipping</option>
                  </select>
                  <button className="btn btn-success" onClick={() => handleSaveField('shipping_method')} disabled={isSaving}><i className="bi bi-check"></i></button>
                  <button className="btn btn-secondary" onClick={() => toggleEdit('shipping_method')}><i className="bi bi-x"></i></button>
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">{job.shipping_method || "Customer Pickup"}</span>
                  <i className="bi bi-pencil-square text-primary" style={{cursor:'pointer'}} onClick={() => toggleEdit('shipping_method')}></i>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Customer PO */}
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title text-muted small">Customer PO</h6>
              {editMode.customer_po ? (
                <div className="input-group input-group-sm">
                  <input type="text" className="form-control" value={editValues.customer_po} onChange={(e) => setEditValues({...editValues, customer_po: e.target.value})} />
                  <button className="btn btn-success" onClick={() => handleSaveField('customer_po')} disabled={isSaving}><i className="bi bi-check"></i></button>
                  <button className="btn btn-secondary" onClick={() => toggleEdit('customer_po')}><i className="bi bi-x"></i></button>
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">{job.customer_po || "N/A"}</span>
                  <i className="bi bi-pencil-square text-primary" style={{cursor:'pointer'}} onClick={() => toggleEdit('customer_po')}></i>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Customer SO */}
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title text-muted small">Cust SO</h6>
              {editMode.customer_so ? (
                <div className="input-group input-group-sm">
                  <input type="text" className="form-control" value={editValues.customer_so} onChange={(e) => setEditValues({...editValues, customer_so: e.target.value})} />
                  <button className="btn btn-success" onClick={() => handleSaveField('customer_so')} disabled={isSaving}><i className="bi bi-check"></i></button>
                  <button className="btn btn-secondary" onClick={() => toggleEdit('customer_so')}><i className="bi bi-x"></i></button>
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">{job.customer_so || "N/A"}</span>
                  <i className="bi bi-pencil-square text-primary" style={{cursor:'pointer'}} onClick={() => toggleEdit('customer_so')}></i>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Priority */}
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title text-muted small">Priority Level</h6>
              {editMode.priority_level ? (
                <div className="input-group input-group-sm">
                  <select className="form-select" value={editValues.priority_level} onChange={(e) => setEditValues({...editValues, priority_level: parseInt(e.target.value)})}>
                    <option value="1">1 - Critical (neg. stock)</option>
                    <option value="2">2 - High</option>
                    <option value="3">3 - Normal</option>
                    <option value="4">4 - Low</option>
                  </select>
                  <button className="btn btn-success" onClick={() => handleSaveField('priority_level')} disabled={isSaving}><i className="bi bi-check"></i></button>
                  <button className="btn btn-secondary" onClick={() => toggleEdit('priority_level')}><i className="bi bi-x"></i></button>
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <span className={`badge bg-${job.priority_level === 1 ? 'danger' : job.priority_level === 2 ? 'warning text-dark' : job.priority_level === 3 ? 'info text-dark' : 'secondary'}`}>Level {job.priority_level}</span>
                  <i className="bi bi-pencil-square text-primary" style={{cursor:'pointer'}} onClick={() => toggleEdit('priority_level')}></i>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Notes */}
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title text-muted small">Job Notes</h6>
              {editMode.notes ? (
                <div className="d-flex flex-column gap-2">
                  <textarea className="form-control form-control-sm" rows="3" value={editValues.notes} onChange={(e) => setEditValues({...editValues, notes: e.target.value})} />
                  <div className="btn-group btn-group-sm">
                    <button className="btn btn-success" onClick={() => handleSaveField('notes')} disabled={isSaving}>Save</button>
                    <button className="btn btn-secondary" onClick={() => toggleEdit('notes')}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="d-flex justify-content-between">
                  <div className="small text-truncate" style={{maxWidth:'80%', whiteSpace:'pre-wrap', maxHeight:'60px', overflowY:'auto'}}>
                    {job.notes || <em>No notes</em>}
                  </div>
                  <i className="bi bi-pencil-square text-primary" style={{cursor:'pointer'}} onClick={() => toggleEdit('notes')}></i>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- Job Files --- */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center bg-light">
          <h6 className="mb-0"><i className="bi bi-paperclip me-2"></i>Job Files (PO, Drawings, etc.)</h6>
          <span className="badge bg-secondary">{jobDocuments.length}</span>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-8 border-end">
              {jobDocuments.length === 0 ? (
                <div className="text-center text-muted py-3">No files attached.</div>
              ) : (
                <div className="list-group list-group-flush">
                  {jobDocuments.map(doc => {
                    const badge = getFileTypeBadge(doc.original_filename);
                    return (
                      <div key={doc.document_id} className="list-group-item d-flex justify-content-between align-items-center px-0">
                        <div className="d-flex align-items-center overflow-hidden">
                          <span className={`badge ${badge.color} me-2`}>{badge.label}</span>
                          <div className="text-truncate">
                            <strong>{doc.display_name}</strong>
                            <div className="small text-muted">{doc.description} &bull; {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : "N/A"}</div>
                          </div>
                        </div>
                        <div className="btn-group">
                          <a href={doc.s3_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary" download><i className="bi bi-download"></i></a>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteDocument(doc.document_id)}><i className="bi bi-trash"></i></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="col-md-4">
              <div className="bg-light p-2 rounded">
                <h6 className="small fw-bold mb-2">Upload New File</h6>
                <input type="text" className="form-control form-control-sm mb-2" placeholder="File Name" value={docDisplayName} onChange={e => setDocDisplayName(e.target.value)} />
                <input type="text" className="form-control form-control-sm mb-2" placeholder="Description" value={docDescription} onChange={e => setDocDescription(e.target.value)} />
                <input type="file" className="form-control form-control-sm mb-2" onChange={e => setDocUploadFile(e.target.files[0] || null)} />
                <button className="btn btn-sm btn-primary w-100" onClick={handleUploadDocument} disabled={isUploadingDoc || !docUploadFile}>{isUploadingDoc ? "Uploading..." : "Upload File"}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Laser Task Table --- */}
      {laserTask && (
        <div className="mb-3">
          <h6>Laser Task</h6>
          <table className="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Task ID</th><th>Workstation</th><th>Status</th><th>Auth User</th>
                <th>Sheet Req</th><th>Sheet Count</th><th>Est Time</th><th>Actual Time</th>
                <th>Scheduled</th><th>Start</th><th>End</th><th>Cut Sheet</th><th>Material</th><th>Queue</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{laserTask.task_id}</td>
                <td>{laserTask.workstation}</td>
                <td><StatusBadge status={laserTask.status} /></td>
                <td>{renderAuthUserSelect(laserTask)}</td>
                <td>{laserTask.sheet_required || 0}</td>
                <td>{laserTask.sheet_count || 0}</td>
                <td>{formatSecondsToHM(intervalToSeconds(laserTask.estimated_time))}</td>
                <td>{formatSecondsToHM(laserTask.elapsed_time_seconds)}</td>
                <td>{formatDateOnly(laserTask.scheduled_date)}</td>
                <td>{formatTimestamp(laserTask.start_time)}</td>
                <td>
                  {formatTimestamp(laserTask.end_time)}
                  {laserTask.completed_by_user_name && (
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      by {laserTask.completed_by_user_name}
                    </div>
                  )}
                </td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    {cutSheetData?.image_path ? (
                      <img src={cutSheetData.image_path} alt="Cut Sheet" className="dark-invertible" style={{ width: "50px", height: "50px", objectFit: "contain", cursor: "pointer" }} onClick={() => setSelectedImage(cutSheetData.image_path)} />
                    ) : (<small className="text-muted">No image</small>)}
                    {cutSheetData?.cut_sheet_name && (
                      <small>{cutSheetData.cut_sheet_name}</small>
                    )}
                  </div>
                </td>
                <td>
                  <small>{cutSheetData?.material_description || cutSheetData?.custom_material_description || <span className="text-muted">--</span>}</small>
                </td>

                <td>
                  <div className="d-flex gap-1">
                    <Link to={`/jobqueue?station=${laserTask.workstation}&task_id=${laserTask.task_id}`} className="btn btn-sm btn-secondary">View</Link>
                    {canOfferCutSheetSwitch(laserTask, job.status) && (
                      <button
                        className="btn btn-sm btn-outline-warning text-nowrap"
                        title="Cut this job from another cut sheet (the same nest on other steel)"
                        onClick={() => setSwitchTaskId(laserTask.task_id)}
                      >
                        Switch sheet
                      </button>
                    )}
                    {canShowVideo(laserTask) && (
                      <button className="btn btn-sm btn-outline-danger" title="View task video" onClick={() => openVideoModal(laserTask)}>
                        <i className="fas fa-video"></i>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* --- Parts List --- */}
      <h6>Ordered Products</h6>
      <table className="table table-sm table-bordered mb-3">
        <thead>
          <tr><th>Product ID</th><th>Description</th><th>Ordered Qty</th><th>Thumbnail</th></tr>
        </thead>
        <tbody>
          {parts.map((p) => (
            <tr key={p.part_id}>
              <td>{p.product_id}</td>
              <td>{p.description}</td>
              <td>{p.quantity}</td>
              <td>
                {p.image_path ? (
                  <img src={p.image_path} alt="Part" className="dark-invertible" style={{ width: "50px", height: "50px", objectFit: "contain", cursor: "pointer" }} onClick={() => setSelectedImage(p.image_path)} />
                ) : (<small className="text-muted">No image</small>)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* --- Sub-Tasks by Product --- */}
      <h6>Sub-Job Tasks by Product</h6>
      {parts.map((part) => {
        const tasksForPart = tasks.filter((t) => t.part_id === part.part_id);
        if (tasksForPart.length === 0) {
          return (
            <div key={part.part_id} className="mb-3">
              <strong>{part.product_id} – {part.description}</strong>
              <div className="text-muted ps-3">No sub-tasks for this item.</div>
            </div>
          );
        }
        return (
          <div key={part.part_id} className="mb-3">
            <strong>{part.product_id} – {part.description}</strong>
            <table className="table table-sm table-bordered mt-1">
              <thead>
                <tr>
                  <th>Task ID</th><th>Workstation</th><th>Status</th><th>Auth User</th>
                  <th>Ord Qty</th><th>Stn Qty</th><th>Est Time</th><th>Actual</th>
                  <th>Scheduled</th><th>Start</th><th>End</th><th>Queue</th>
                </tr>
              </thead>
              <tbody>
                {tasksForPart.map((t) => (
                  <tr key={t.task_id}>
                    <td>{t.task_id}</td><td>{t.workstation}</td>
                    <td><StatusBadge status={t.status} /></td>
                    <td>{renderAuthUserSelect(t)}</td>
                    <td>{part.quantity}</td><td>{t.quantity || 0}</td>
                    <td>{formatSecondsToHM(intervalToSeconds(t.estimated_time))}</td>
                    <td>{formatSecondsToHM(t.elapsed_time_seconds)}</td>
                    <td>{formatDateOnly(t.scheduled_date)}</td>
                    <td>{formatTimestamp(t.start_time)}</td>
                    <td>
                      {formatTimestamp(t.end_time)}
                      {t.completed_by_user_name && (
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                          by {t.completed_by_user_name}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Link to={`/jobqueue?station=${t.workstation}&task_id=${t.task_id}`} className="btn btn-sm btn-secondary">View</Link>
                        {canShowVideo(t) && (
                          <button className="btn btn-sm btn-outline-danger" title="View task video" onClick={() => openVideoModal(t)}>
                            <i className="fas fa-video"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* --- Unlinked Tasks --- */}
      {otherUnlinkedTasks.length > 0 && (
        <div>
          <h6>Other Unlinked Tasks</h6>
          <table className="table table-sm table-bordered">
            <thead>
              <tr><th>Task ID</th><th>Workstation</th><th>Status</th><th>Auth User</th><th>Est Time</th><th>Actual</th><th>Scheduled</th><th>Start</th><th>End</th><th>Queue</th></tr>
            </thead>
            <tbody>
              {otherUnlinkedTasks.map((t) => (
                <tr key={t.task_id}>
                  <td>{t.task_id}</td><td>{t.workstation}</td><td><StatusBadge status={t.status} /></td>
                  <td>{renderAuthUserSelect(t)}</td>
                  <td>{formatSecondsToHM(intervalToSeconds(t.estimated_time))}</td>
                  <td>{formatSecondsToHM(t.elapsed_time_seconds)}</td><td>{formatDateOnly(t.scheduled_date)}</td>
                  <td>{formatTimestamp(t.start_time)}</td>
                  <td>
                    {formatTimestamp(t.end_time)}
                    {t.completed_by_user_name && (
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        by {t.completed_by_user_name}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Link to={`/jobqueue?station=${t.workstation}&task_id=${t.task_id}`} className="btn btn-sm btn-secondary">View</Link>
                      {canShowVideo(t) && (
                        <button className="btn btn-sm btn-outline-danger" title="View task video" onClick={() => openVideoModal(t)}>
                          <i className="fas fa-video"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CutSheetSwitchModal
        show={switchTaskId != null}
        taskId={switchTaskId}
        onHide={() => setSwitchTaskId(null)}
        onSwitched={onJobChanged}
      />

      {/* --- Image Modal --- */}
      {selectedImage && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", cursor: "zoom-out" }} onClick={() => setSelectedImage(null)}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "90%", width: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ background: "transparent", border: "none" }}>
              <img src={selectedImage} alt="Full view" style={{ maxWidth: "90vw", maxHeight: "90vh", margin: "auto", display: "block", cursor: "zoom-out" }} onClick={() => setSelectedImage(null)} />
            </div>
          </div>
        </div>
      )}

      {/* --- Video Playback Modal --- */}
      {videoTask && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1060 }} onClick={closeVideoModal}>
          <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '95vw' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content bg-dark text-white border-secondary">
              <div className="modal-header border-secondary">
                <h5 className="modal-title">
                  <i className="fas fa-video me-2 text-danger"></i>
                  Task Video — {videoTask.workstation} — Job #{job.job_id}
                </h5>
                <div className="text-muted ms-3" style={{ fontSize: '0.85rem' }}>
                  {formatTimeCT(videoTask.start_time)} — {formatTimeCT(videoTask.end_time)} CT
                </div>
                <button type="button" className="btn-close btn-close-white" onClick={closeVideoModal}></button>
              </div>

              <div className="modal-body" style={{ minHeight: '50vh' }}>
                {videoLoading && Object.keys(videoUrls).length === 0 ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-light mb-3" role="status"></div>
                    <div className="text-muted">Loading recordings...</div>
                  </div>
                ) : videoError && Object.keys(videoUrls).length === 0 ? (
                  <div className="text-center p-5">
                    <div className="text-warning mb-3">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {videoError}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                      {Array.from({ length: videoCamCount }, (_, camIdx) => (
                        <div key={camIdx} className="text-center" style={{ flex: videoCamCount <= 2 ? '1 1 48%' : '1 1 31%', maxWidth: videoCamCount === 1 ? '100%' : videoCamCount === 2 ? '49%' : '33%' }}>
                          <div className="mb-1" style={{ fontSize: '0.8rem', color: '#aaa' }}>Camera {camIdx + 1}</div>
                          {videoUrls[camIdx] ? (
                            <video
                              key={videoUrls[camIdx]}
                              ref={el => { videoRefs.current[camIdx] = el; }}
                              src={videoUrls[camIdx]}
                              controls={camIdx === 0}
                              muted={camIdx !== 0}
                              style={{ width: '100%', backgroundColor: '#000', borderRadius: '4px' }}
                              onPlay={() => {
                                Object.entries(videoRefs.current).forEach(([idx, el]) => {
                                  if (parseInt(idx) !== 0 && el && el.paused) {
                                    el.currentTime = videoRefs.current[0]?.currentTime || 0;
                                    el.play().catch(() => {});
                                  }
                                });
                              }}
                              onPause={() => {
                                if (camIdx === 0) {
                                  Object.entries(videoRefs.current).forEach(([idx, el]) => {
                                    if (parseInt(idx) !== 0 && el && !el.paused) el.pause();
                                  });
                                }
                              }}
                              onSeeked={() => {
                                if (camIdx === 0) {
                                  const ct = videoRefs.current[0]?.currentTime || 0;
                                  Object.entries(videoRefs.current).forEach(([idx, el]) => {
                                    if (parseInt(idx) !== 0 && el) el.currentTime = ct;
                                  });
                                }
                              }}
                              onEnded={camIdx === 0 ? handleVideoEnded : undefined}
                            />
                          ) : (
                            <div style={{ backgroundColor: '#111', borderRadius: '4px', padding: '3rem 1rem', color: '#666' }}>
                              {videoLoading ? "Loading..." : "No clip"}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {videoLoading && Object.keys(videoUrls).length > 0 && (
                      <div className="text-center mt-2">
                        <div className="spinner-border spinner-border-sm text-light me-2" role="status"></div>
                        <small className="text-muted">Loading next clip...</small>
                      </div>
                    )}
                  </>
                )}
              </div>

              {maxClipCount > 0 && (
                <div className="modal-footer border-secondary d-flex justify-content-between align-items-center">
                  <button className="btn btn-sm btn-outline-light" disabled={currentClipIdx === 0 || videoLoading} onClick={() => goToClip(currentClipIdx - 1)}>
                    <i className="fas fa-chevron-left me-1"></i> Previous
                  </button>
                  <div className="d-flex align-items-center gap-2 flex-wrap justify-content-center" style={{ maxWidth: '70%' }}>
                    {(() => {
                      const cam0Clips = clipsByCamera[0] || [];
                      const showAll = cam0Clips.length <= 20;
                      if (cam0Clips.length === 0) return <span className="text-muted">No clips</span>;
                      return (
                        <>
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>Clip {currentClipIdx + 1} / {maxClipCount}</span>
                          {showAll && cam0Clips.map((f, i) => (
                            <button key={f} className={`btn btn-sm ${i === currentClipIdx ? 'btn-danger' : 'btn-outline-secondary'}`} style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem' }} disabled={videoLoading} onClick={() => goToClip(i)}>
                              {clipTimeCT(f)}
                            </button>
                          ))}
                          {!showAll && <span className="text-muted" style={{ fontSize: '0.75rem' }}>{clipTimeCT(cam0Clips[currentClipIdx] || "")}</span>}
                        </>
                      );
                    })()}
                  </div>
                  <button className="btn btn-sm btn-outline-light" disabled={currentClipIdx >= maxClipCount - 1 || videoLoading} onClick={() => goToClip(currentClipIdx + 1)}>
                    Next <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreShipModal({ job, apiBaseUrl, onClose, onDone }) {
  const [parts, setParts] = useState([]);
  const [qtys, setQtys] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await loadShipParts(job.job_id);
        if (alive) setParts(data);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiBaseUrl, job.job_id]);

  const remainingFor = remainingToShip;

  // features/jobs/jobActions.js: the quantities are checked before any PIN, then PIN + POST.
  const handleSubmit = async () => {
    setError("");
    const plan = preShipItems(parts, qtys);
    if (plan.error) { setError(plan.error); return; }
    const result = await preShip({ jobId: job.job_id, items: plan.items, ui: desktopUi, setBusy: setSubmitting });
    if (result.error) setError(result.error);
    if (result.done) {
      if (onDone) onDone();
      onClose();
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1"
         style={{ background: "rgba(0,0,0,0.5)", zIndex: 1060 }} onClick={onClose}>
      <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Pre-ship — Job {job.master_job_num || job.job_id}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <p className="text-muted small mb-3">
              Carve a quantity to ship <strong>now</strong>, before the job finishes. It drops into the
              normal shipping queue and (for Bad Idea stock jobs) is auto-received into inventory. The
              remainder ships when production completes — no double-count.
            </p>
            {parts.some((p) => p.component_of_assembly) && (
              <p className="text-muted small mb-3">
                <i className="bi bi-info-circle me-1"></i>
                Assembly components (e.g. heat shields) ship automatically with their
                assembly — enter a quantity only on the assembly's primary part.
              </p>
            )}
            {error && <div className="alert alert-danger py-2">{error}</div>}
            {loading ? (
              <div>Loading parts…</div>
            ) : (
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Shipped</th>
                    <th className="text-end">Remaining</th>
                    <th style={{ width: 130 }}>Pre-ship qty</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((p) => {
                    const rem = remainingFor(p);
                    return (
                      <tr key={p.part_id}>
                        <td>
                          <strong>{p.product_id}</strong>
                          <div className="text-muted small">{p.description}</div>
                        </td>
                        <td className="text-end">{p.quantity_ordered}</td>
                        <td className="text-end">{p.quantity_shipped}</td>
                        <td className="text-end">{rem}</td>
                        <td>
                          {p.component_of_assembly ? (
                            <span className="text-muted small fst-italic">
                              Ships with {p.component_of_assembly}
                            </span>
                          ) : (
                            <input type="number" min="0" max={rem}
                                   className="form-control form-control-sm"
                                   value={qtys[p.part_id] || ""} disabled={rem <= 0}
                                   onChange={(e) => setQtys((q) => ({ ...q, [p.part_id]: e.target.value }))} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? "Pre-shipping…" : "Pre-ship → Shipping queue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MasterJobOverviewPage() {
  const [jobs, setJobs] = useState([]);
  const [preShipJob, setPreShipJob] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [hideShipped, setHideShipped] = useState(true);
  const [customerFilter, setCustomerFilter] = useState("");
  const [poFilter, setPoFilter] = useState("");
  const [soFilter, setSoFilter] = useState("");
  const [queueFilter, setQueueFilter] = useState("");
  
  const [startDate, setStartDate] = useState(() => defaultListStartDate());

  const [expandedJobId, setExpandedJobId] = useState(null);
  const [editingDueJobId, setEditingDueJobId] = useState(null);
  const [tempDueDate, setTempDueDate] = useState(null);
  const [compactView, setCompactView] = useState(false);
  const [sortColumn, setSortColumn] = useState("job_id");
  const [sortOrder, setSortOrder] = useState("desc");

  // --- DEBOUNCE LOGIC ---
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay

  const API_BASE = config.API_BASE_URL;

  useEffect(() => {
    async function loadCustomers() {
      try {
        setCustomers(await loadCustomersByName());
      } catch (err) {
        console.error("Error loading customers:", err);
      }
    }
    loadCustomers();
  }, [API_BASE]);

  // --- REFACTORED: useEffect now triggers on debounced search term ---
  useEffect(() => {
    // A filter change starts a new load; the older one (several pages now) must not land after it.
    let cancelled = false;
    const loadJobs = async () => {
      setLoading(true);
      setError(null);
      try {
        // The debounced search term searches everything (other filters off); "hide shipped" always applies.
        const params = jobListParams({
          searchTerm: debouncedSearchTerm, customerId: customerFilter, customerPo: poFilter, customerSo: soFilter,
          startDate, queueFilter, hideShipped,
        });
        // Page by page: the whole list in one response passed Lambda's 6 MB limit (jobPages.js).
        const data = await loadJobList(params, { isCancelled: () => cancelled });
        if (data === null) return;
        setJobs(data);

      } catch (err) {
        if (cancelled) return;
        console.error("Error loading jobs:", err);
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadJobs();
    return () => { cancelled = true; };
  // This effect re-runs whenever the debounced search term changes, or when other filters change.
  }, [API_BASE, debouncedSearchTerm, customerFilter, poFilter, soFilter, startDate, queueFilter, hideShipped, refreshTick]);

  function isOverdue(job) {
    if (!job.due_date) return false;
    const due = parseLocalDateOnly(job.due_date) || new Date(job.due_date);
    return due < new Date() && !["Completed", "Shipped"].includes(job.status);
  }

  function isInProgress(job) {
    return job.status === "In Progress";
  }

  const filteredAndSortedJobs = React.useMemo(() => sortJobs(jobs, sortColumn, sortOrder), [jobs, sortColumn, sortOrder]);

  function handleSort(column) {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  }

  function toggleExpand(jobId) {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
    } else {
      setExpandedJobId(jobId);
    }
  }

  async function handleCancelJob(jobId) {
    if (!window.confirm("Are you sure you want to CANCEL this job?")) return;
    try {
      const res = await fetch(`${API_BASE}/jobs/${jobId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Cancelled" }),
      });
      if (!res.ok) {
        const errJson = await res.json();
        alert("Cancel job failed: " + (detailToString(errJson.detail) || res.status));
        return;
      }
      // Re-trigger the main data fetch after cancelling
      // This is simpler than trying to manually update state
      setSearchTerm(''); // Clearing filters might be a good UX here
      setQueueFilter('');
    } catch (err) {
      console.error("Error cancelling job:", err);
      alert("Error cancelling job.");
    }
  }

  function handleDueDateClick(job) {
    if (!canEditDueDate(job)) {
      alert("Cannot edit due date for completed, shipped, or cancelled jobs.");
      return;
    }
    setEditingDueJobId(job.job_id);
    let currentVal = null;
    if (job.due_date) {
      const parsed = parseLocalDateOnly(job.due_date) || new Date(job.due_date);
      if (!isNaN(parsed.getTime())) currentVal = parsed;
    }
    setTempDueDate(currentVal);
  }

  async function handleDateChange(jobId, newDate) {
    setTempDueDate(newDate);
    if (!newDate) {
      alert("Invalid date chosen. Reverting...");
      setEditingDueJobId(null);
      return;
    }
    const yyyyMmDd = newDate.toISOString().split("T")[0];
    try {
      await patchJob(jobId, { due_date: yyyyMmDd });
      alert("Due date updated successfully!");
      // Manually update the specific job in state to avoid a full reload
      setJobs(prevJobs => prevJobs.map(job => 
        job.job_id === jobId ? { ...job, due_date: yyyyMmDd } : job
      ));
    } catch (err) {
      console.error("Error updating due date:", err);
      alert("Failed to update due date: " + err.message);
    } finally {
      setEditingDueJobId(null);
    }
  }

  async function handleUpdateDueDates() {
    try {
      const res = await fetch(`${API_BASE}/jobs/run_hybrid_update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`Error from server: ${res.status}`);
      const result = await res.json();
      alert(result.message || "Due Dates updated successfully!");
      // Trigger a reload by changing one of the dependencies of the main useEffect
      setQueueFilter(prev => prev); // A simple way to trigger refetch
    } catch (err) {
      alert("Failed to update due dates: " + err.message);
    }
  }
  
  return (
    <div className="mb-5">
      <style>{`.jobs-table td, .jobs-table th { padding: 0.6rem 0.75rem; }
.jobs-table thead th { position: sticky; top: 0; background-color: var(--jq-bg-card, #fff); z-index: 10; font-weight: 600; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--jq-text-muted, rgba(0,0,0,0.5)); }`}</style>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">
          Master Job Overview
          {!loading && <span className="badge bg-secondary ms-2" style={{ fontSize: "0.5em", verticalAlign: "middle" }}>{filteredAndSortedJobs.length}</span>}
        </h2>
        <div>
          <button className="btn btn-outline-secondary me-2" onClick={() => setCompactView(!compactView)}>
            <i className="bi bi-list"></i> {compactView ? "Detailed View" : "Compact View"}
          </button>
          <button className="btn btn-success me-2" onClick={handleUpdateDueDates}>Update Due Dates</button>
          <Link to="/job-entry" className="btn btn-primary">Create New Order</Link>
        </div>
      </div>

      <div className="row mb-3 align-items-end g-2">
        <div className="col-md-3">
          <input type="text" placeholder="Search all jobs..." className="form-control form-control-sm" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} disabled={!!searchTerm}>
            <option value="">All Customers</option>
            {customers.map((cust) => (<option key={cust.customer_id} value={cust.customer_id}>{cust.name}</option>))}
          </select>
        </div>
        <div className="col-md-2">
          <input type="text" className="form-control form-control-sm" placeholder="Customer PO..." value={poFilter} onChange={(e) => setPoFilter(e.target.value)} disabled={!!searchTerm} />
          <input type="text" className="form-control form-control-sm mt-2" placeholder="Cust SO..." value={soFilter} onChange={(e) => setSoFilter(e.target.value)} disabled={!!searchTerm} />
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={queueFilter} onChange={(e) => setQueueFilter(e.target.value)} disabled={!!searchTerm}>
            <option value="">All Stations</option>
            {JOB_LIST_STATIONS.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <div className="d-flex align-items-center gap-1">
            <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} className="form-control form-control-sm" dateFormat="MM/dd/yyyy" disabled={!!searchTerm} placeholderText="Since date..." />
            {startDate && <button className="btn btn-sm btn-outline-secondary px-1" onClick={() => setStartDate(null)} title="Clear date" disabled={!!searchTerm} style={{ lineHeight: 1 }}><i className="bi bi-x"></i></button>}
          </div>
        </div>
        <div className="col-md-1 d-flex align-items-center justify-content-end">
          <div className="form-check form-switch mb-0">
            <input type="checkbox" className="form-check-input" id="hideShippedJobs" checked={hideShipped} onChange={() => setHideShipped(!hideShipped)} />
            <label htmlFor="hideShippedJobs" className="form-check-label" style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>Shipped</label>
          </div>
        </div>
      </div>
      {searchTerm && <div className="text-muted mb-2" style={{ fontSize: "0.8rem" }}><i className="bi bi-info-circle me-1"></i>Filters disabled during search</div>}
      
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status"></div>
          <div className="text-muted">Loading jobs...</div>
        </div>
      ) : error ? ( <div className="alert alert-danger">Error: {error.message}</div>
      ) : filteredAndSortedJobs.length === 0 ? (
        <div className="text-center py-5">
          <i className="bi bi-inbox" style={{ fontSize: "3rem", color: "var(--jq-text-muted, #999)" }}></i>
          <div className="text-muted mt-2">No jobs found matching your filters.</div>
          <button className="btn btn-sm btn-outline-secondary mt-2" onClick={() => { setSearchTerm(""); setCustomerFilter(""); setPoFilter(""); setSoFilter(""); setQueueFilter(""); setStartDate(null); }}>Clear All Filters</button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover jobs-table">
            <thead className="table-light">
              <tr>
                <th style={{ width: "40px" }}></th>
                <th>Customer</th>
                <th>Customer PO</th>
                <th>Cust SO</th>
                <th onClick={() => handleSort("master_job_num")} style={{ cursor: "pointer" }}>Master #{sortColumn === "master_job_num" && (sortOrder === "asc" ? " ▲" : " ▼")}</th>
                <th onClick={() => handleSort("job_id")} style={{ cursor: "pointer" }}>Job ID{sortColumn === "job_id" && (sortOrder === "asc" ? " ▲" : " ▼")}</th>
                <th onClick={() => handleSort("job_description")} style={{ cursor: "pointer" }}>Description{sortColumn === "job_description" && (sortOrder === "asc" ? " ▲" : " ▼")}</th>
                <th onClick={() => handleSort("status")} style={{ cursor: "pointer" }}>Status{sortColumn === "status" && (sortOrder === "asc" ? " ▲" : " ▼")}</th>
                {!compactView && <th onClick={() => handleSort("priority_level")} style={{ cursor: "pointer" }}>Priority{sortColumn === "priority_level" && (sortOrder === "asc" ? " ▲" : " ▼")}</th>}
                <th onClick={() => handleSort("due_date")} style={{ cursor: "pointer" }}>Due Date{sortColumn === "due_date" && (sortOrder === "asc" ? " ▲" : " ▼")}</th>
                {!compactView && <th>Sheets (Laser)</th>}
                {!compactView && <th>Laser Start</th>}
                {!compactView && <th>Notes</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedJobs.map((job) => {
                const isExpanded = expandedJobId === job.job_id;
                const overdue = isOverdue(job);
                const inProg = isInProgress(job);
                let rowClass = "";
                if (overdue) rowClass = "table-danger fw-bold";
                else if (inProg) rowClass = "table-warning fw-semibold";
                
                const laserTask = job.tasks.find(t => t.workstation === 'Laser');

                return (
                  <React.Fragment key={job.job_id}>
                    <tr className={rowClass} onClick={() => toggleExpand(job.job_id)} style={{ cursor: "pointer" }}>
                      <td><button className="btn btn-sm btn-outline-secondary" onClick={(e) => { e.stopPropagation(); toggleExpand(job.job_id); }}>{isExpanded ? <i className="bi bi-dash-circle"></i> : <i className="bi bi-plus-circle"></i>}</button></td>
                      <td>{job.customer_name || ""}</td>
                      <td>{job.customer_po || ""}</td>
                      <td>{job.customer_so || ""}</td>
                      <td>{highlightSearch(searchTerm, job.master_job_num || "")}</td>
                      <td>{String(job.job_id)}</td>
                      <td>
                        {highlightSearch(searchTerm, job.job_description || "")}
                        <JobProgress tasks={job.tasks} />
                      </td>
                      <td><StatusBadge status={job.status} /></td>
                      {!compactView && <td>{job.priority_level == null ? "" : job.priority_level}</td>}
                      <td style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); handleDueDateClick(job); }}>
                        {editingDueJobId === job.job_id ? (
                          <DatePicker selected={tempDueDate} onChange={(date) => handleDateChange(job.job_id, date)} onClickOutside={() => setEditingDueJobId(null)} placeholderText="Select date" autoFocus />
                        ) : <DueDateDisplay dueDate={job.due_date} status={job.status} />}
                      </td>
                      {!compactView && <td>{laserTask ? laserTask.sheet_required : ''}</td>}
                      {!compactView && <td>{laserTask ? formatTimestamp(laserTask.start_time) : ''}</td>}
                      {!compactView && <td>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "250px" }} title={job.notes || ""}>
                          {job.notes || ""}
                        </div>
                      </td>}
                      <td onClick={(e) => e.stopPropagation()}>
                        {canPreShip(job) && (
                          <button className="btn btn-sm btn-outline-primary me-1" title="Ship a partial quantity now, before the job finishes" onClick={() => setPreShipJob(job)}>
                            <i className="bi bi-box-seam me-1"></i>Pre-ship
                          </button>
                        )}
                        {job.status === "Pending" && (
                          <button className="btn btn-sm btn-danger" onClick={() => handleCancelJob(job.job_id)}>
                            <i className="bi bi-x-circle-fill me-1"></i>Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className={rowClass}>
                        <td></td>
                        <td colSpan={compactView ? 10 : 13}>
                          <ExpandedJobDetail job={job} apiBaseUrl={API_BASE} onJobChanged={() => setRefreshTick((t) => t + 1)} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {preShipJob && (
        <PreShipModal
          job={preShipJob}
          apiBaseUrl={API_BASE}
          onClose={() => setPreShipJob(null)}
          onDone={() => setRefreshTick((t) => t + 1)}
        />
      )}
    </div>
  );
}